import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

export const updateReservationSkill: Skill = {
  id: "updateReservation",
  description:
    "Actualiza una reserva existente (cambiar fecha/hora, mesa, comensales, estado).",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      reservationId: { type: "string" },
      scheduledAt: { type: "string" },
      tableId: { type: "string" },
      partySize: { type: "number" },
      status: { type: "string", enum: ["confirmed", "seated", "cancelled", "no_show"] },
      notes: { type: "string" },
    },
    required: ["restaurantId", "reservationId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const reservationId = String(args.reservationId ?? "");
    if (!restaurantId || !reservationId) return { ok: false, error: "Faltan IDs." };
    const patch: Record<string, unknown> = {};
    const details: Array<{ label: string; value: string }> = [];
    if (args.scheduledAt) {
      patch.scheduledAt = new Date(String(args.scheduledAt)).toISOString();
      details.push({ label: "Nueva fecha", value: patch.scheduledAt as string });
    }
    if (args.tableId) { patch.tableId = String(args.tableId); details.push({ label: "Nueva mesa", value: "—" }); }
    if (Number.isFinite(Number(args.partySize))) { patch.partySize = Number(args.partySize); details.push({ label: "Comensales", value: `${patch.partySize}` }); }
    if (args.status) { patch.status = String(args.status); details.push({ label: "Estado", value: patch.status as string }); }
    if (args.notes !== undefined) patch.notes = String(args.notes);
    if (Object.keys(patch).length === 0) return { ok: false, error: "Nada que actualizar." };

    const ok = await ctx.confirmation.confirm({
      title: "Actualizar reserva",
      details,
      confirmLabel: "Actualizar",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Actualizando reserva…",
      successMsg: "✓ Reserva actualizada",
      restaurantId,
      targetTab: "reservas",
      targetTabLabel: "Reservas",
      run: async () => {
        try {
          await apiWithAuth.patch(
            `/restaurants/${restaurantId}/reservations/${reservationId}`,
            patch,
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "reservations", action: "updated", restaurantId, id: reservationId });
          return { ok: true as const, result: { reservationId, ...patch }, humanSummary: "Reserva actualizada." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const deleteReservationSkill: Skill = {
  id: "deleteReservation",
  description: "Cancela / elimina una reserva.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      reservationId: { type: "string" },
    },
    required: ["restaurantId", "reservationId"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const reservationId = String(args.reservationId ?? "");
    if (!restaurantId || !reservationId) return { ok: false, error: "Faltan IDs." };

    const ok = await ctx.confirmation.confirm({
      title: "Cancelar reserva",
      description: "Se eliminará la reserva del calendario.",
      confirmLabel: "Cancelar reserva",
      danger: true,
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Cancelando reserva…",
      successMsg: "✓ Reserva cancelada",
      restaurantId,
      targetTab: "reservas",
      targetTabLabel: "Reservas",
      run: async () => {
        try {
          await apiWithAuth.delete(
            `/restaurants/${restaurantId}/reservations/${reservationId}`,
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "reservations", action: "deleted", restaurantId, id: reservationId });
          return { ok: true as const, result: { reservationId }, humanSummary: "Reserva cancelada." };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
